import { createApi } from "@reduxjs/toolkit/query/react";

import { Auth0Client } from "@/services/Auth";
import baseQueryWithOptions from "@/store/api/baseQueryWithOptions";
import { isWeb } from "@/utils/platform.util";

enum CacheTagType {
  AppointmentEvents = "appointment_events",
  AssessmentLastUpdatedUsers = "assessment_last_updated_users",
  Avatar = "avatar",
  BasicInfo = "basic_info",
  Consents = "consents",
  FullProfile = "full_profile",
  GroupClassEvents = "group_class_events",
  MealsConfiguration = "meals_configuration",
  PatientDocuments = "patient_documents",
  SendbirdToken = "sendbird_token",
  SMTEntries = "smt_entries",
  SMTEntry = "smt_entry",
  SMTSummary = "smt_summary",
  WeightObservations = "weight_observations",
}

enum CacheIdType {
  AppUser = "app_user",
}

const api = createApi({
  baseQuery: baseQueryWithOptions,
  endpoints: (build) => ({
    resetApiState: build.mutation<string, void>({
      // eslint-disable-next-line max-params
      queryFn: async (_, { dispatch }) => {
        // on web, clearCredentials is not used, instead clearWebSession is invoked elsewhere
        if (!isWeb()) {
          // this is to reset baseQueryWithOption.prepareHeaders, which invokes
          // auth0Client.credentialsManager.getCredentials() on every API request

          // NOTE: do NOT remove or refactor this line out of this endpoint/function
          // see JN-1043 for full details as to why this must stay here
          await Auth0Client.credentialsManager.clearCredentials();
        }

        await dispatch(api.util.resetApiState());
        return { data: "success" };
      },
    }),
  }),
  // eslint-disable-next-line @typescript-eslint/naming-convention
  refetchOnReconnect: true,
  // See: https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#cache-tags
  tagTypes: [
    CacheTagType.AppointmentEvents,
    CacheTagType.AssessmentLastUpdatedUsers,
    CacheTagType.Avatar,
    CacheTagType.BasicInfo,
    CacheTagType.Consents,
    CacheTagType.FullProfile,
    CacheTagType.GroupClassEvents,
    CacheTagType.MealsConfiguration,
    CacheTagType.PatientDocuments,
    CacheTagType.SendbirdToken,
    CacheTagType.WeightObservations,
    CacheTagType.SMTSummary,
    CacheTagType.SMTEntries,
    CacheTagType.SMTEntry,
  ],
});

const { useResetApiStateMutation } = api;

export { CacheIdType, CacheTagType, useResetApiStateMutation };
export default api;
